<script>
import {defineComponent} from 'vue';
import {getGoogleMapsApi, isValidLatLng, isMobile, duplicateObject} from '../setup/shared';
import AffiliateCard from './components/AffiliateCard.vue';
import ContactForm from './components/ContactForm.vue';
import BasicModal from '../vue-components/BasicModal.vue';
import qs from 'query-string';

const QS_ARGS = { arrayFormat: 'separator', arrayFormatSeparator: '|' };

const URL_PARAMS = {
    Location: 'l',
    CountryCode: 'cc',
    UserLocation: 'cl',
    Lat: 'lat',
    Lng: 'lng',
    AffiliateTypes: 'vts',
};

const GMAPS_CHECK_INTERVAL_MS = 250;
const GMAPS_FORCE_REFRESH_DELAY_MS = 1000;

/**
 * For optimization search is never triggered reactively,
 * and instead is run inside loop with given interval when requested.
 */
const SEARCH_LOOP_INTERVAL_MS = 250;

/**
 * Determines how long to wait for user input before actually running search.
 * Must be at least twice as long as SEARCH_LOOP_INTERVAL_MS
 */
const SEARCH_CONTINUOUS_INPUT_MS = SEARCH_LOOP_INTERVAL_MS*3;

/**
 * Maximum radius around searched location (in meters), within which any number of affiliates would be shown
 * @type {number}
 */
const MAX_RADIUS_ANY_NUM_AFFILIATES = 100000;

/**
 * Max. radius where to include any number of neighboring affiliates around every already found affiliate (in meters).
 * For affiliates within MAX_RADIUS_ANY_NUM_AFFILIATES it would effectively "add" to the radius
 * @type {number}
 */
const MAX_RADIUS_NEIGHBORS_LOOKUP = 25000;

/**
 * Minimum affiliates to show in location search
 * @type {number}
 */
const MIN_NUM_LOCATION_DISTANCE_RESULTS = 2;


export default defineComponent({
    name: 'AffiliateMapApp',

    components: { AffiliateCard, BasicModal, ContactForm },

    props: {
        affiliates: {
            type: Array,
            default: () => [],
        }
    },

    data() {
        return {

            filtersVisible: false,

            locationInputInFocus: false,
            locationInput: '',
            location: null,
            locationAddress: '',
            isUserLocation: false,

            // Set while querying google for geocoding
            isQueryingLocation: false,
            locationNotFound: false,

            gmaps: null,
            gmap: null,
            mapZoom: 7,
            mapCenter: {lat: 59.334591, lng: 18.063240},
            forcedMapRefresgTimeoutId: null,

            activeOutputTab: 'list',
            affiliateTypes: [this.$affiliateTypes.service, this.$affiliateTypes.dealer],
            isMobile: isMobile(),

            urlQuery: {},

            filteredAffiliates: [],
            filteredAffiliatesComplement: [],

            // Set while running filter and rendering elements, to effectively debounce it
            // Primarily for low-end computers where it could take longer time
            isAffiliatesFilterRunning: false,
            affiliatesFilterRequested: false,
            affiliatesFilterRequestTimestamp: 0,
            affiliatesFilterLoop: null,

            affiliateToContact: null,
        };
    },

    computed: {
        isUnavailable() {
            return this.isQueryingLocation;
        },

        countriesWithAffiliates() {
            const countries = {};
            for (let affiliate of this.filteredAffiliates) {
                const country = affiliate.countries[0];
                if (!countries[country.a2]) {
                    countries[country.a2] = Object.assign({}, country, {
                        affiliates: [],
                    });
                }
                countries[country.a2].affiliates.push(affiliate);
            }
            const countriesArr = Object.values(countries);
            if (!this.location) {
                countriesArr.sort((a, b) => {
                    return a.name > b.name ? 1 : (a.name < b.name ? -1 : 0);
                });
            }
            return countriesArr;
        },
    },

    watch: {
        filteredAffiliates(affiliates, prevAffiliates) {
            const sameResults = affiliates.length === prevAffiliates.length &&
                affiliates.every(c => prevAffiliates.some(cl => c.id === cl.id));
            if (!sameResults) {
                this.resetListView();
                this.$nextTick(() => {
                    this.centerMapOnAffiliates(affiliates);
                });
            }
        },
        affiliateTypes(vts) {
            this.urlQuery = duplicateObject(Object.assign(
                {},
                this.urlQuery,
                { [URL_PARAMS.AffiliateTypes]: vts.join(',') }
            ));
            this.requestAffiliatesFilter({ immediate: true });
        },
        location(location) {
            this.locationAddress = location?.formattedAddress;
            this.isUserLocation = location?.isUserLocation;
            if (location?.isUserLocation) {
                this.urlQuery = duplicateObject(Object.assign(
                    {},
                    this.urlQuery,
                    { [URL_PARAMS.UserLocation]: 1 }
                ), {
                    exclude: [URL_PARAMS.Location, URL_PARAMS.Lng, URL_PARAMS.Lat],
                });
            } else if (location?.formattedAddress) {
                const data = { [URL_PARAMS.Location]: location.formattedAddress };
                const exclude = [URL_PARAMS.UserLocation, URL_PARAMS.Lng, URL_PARAMS.Lat];
                if (location.countryCode) {
                    data[URL_PARAMS.CountryCode] = location.countryCode;
                } else {
                    exclude.push(URL_PARAMS.CountryCode);
                }
                this.urlQuery = duplicateObject(Object.assign({}, this.urlQuery, data), { exclude });
            } else {
                this.urlQuery = duplicateObject(this.urlQuery, {
                    exclude: [URL_PARAMS.UserLocation, URL_PARAMS.Location, URL_PARAMS.Lng, URL_PARAMS.Lat],
                });
            }
            this.requestAffiliatesFilter();
        },
        locationInput() {
            this.requestAffiliatesFilter();
            this.$nextTick(() => this.updateResetButtonPosition());
        },
        urlQuery(query) {
            const keys = Object.keys(query);
            let url = window.location.origin +
                window.location.pathname +
                (window.location.hash.length > 1 ? window.location.hash : '');

            if (keys.length) {
                url += '?' + qs.stringify(query, QS_ARGS);
            }

            if (url !== window.location.href) {
                window.history.replaceState({}, null, url);
            }
        },
    },

    created() {
        this.urlQuery = qs.parse(window.location.search, QS_ARGS);
        this.$events.once('gmaps-loaded', () => {
            if (this.urlQuery[URL_PARAMS.UserLocation]) {
                this.searchByUserLocation();
            } else if (this.urlQuery[URL_PARAMS.Location]) {
                this.locationInput = this.urlQuery[URL_PARAMS.Location];
                this.locateAndSearch();
            } else if (this.urlQuery[URL_PARAMS.Lng] && this.urlQuery[URL_PARAMS.Lat]) {
                this.location = {
                    lng: parseFloat(this.urlQuery[URL_PARAMS.Lng]),
                    lat: parseFloat(this.urlQuery[URL_PARAMS.Lat]),
                };
            }
        });

        this.requestAffiliatesFilter();
        this.applyAffiliatesFilter();
        this.affiliatesFilterLoop = setInterval(() => {
            this.affiliatesFilterChecker();
        }, SEARCH_LOOP_INTERVAL_MS);
    },

    mounted() {
        this.$events.once('gmaps-loaded', () => {
            this.centerMapOnAffiliates(this.filteredAffiliates);
            this.$forceUpdate();
        });

        if (!this.gmaps || !this.gmap) {
            let gmChecker = setInterval(() => {
                const gmaps = getGoogleMapsApi();
                const gmap = this.$refs.gmap?.$mapObject;
                if (gmaps && gmap) {
                    this.gmaps = gmaps;
                    this.gmap = gmap;
                    this.$events.emit('gmaps-loaded');
                    window.clearInterval(gmChecker);
                }
            }, GMAPS_CHECK_INTERVAL_MS);
        }
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('resize', this.onWindowResize);
        window.addEventListener('orientationchange', this.onWindowResize);
    },

    beforeDestroy() {
        window.clearInterval(this.affiliatesFilterLoop);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('resize', this.onWindowResize);
        window.removeEventListener('orientationchange', this.onWindowResize);
    },

    methods: {

        str() {},

        requestAffiliatesFilter(args = {}) {
            const { immediate } = Object.assign({ immediate: false }, args);
            this.affiliatesFilterRequested = true;
            this.affiliatesFilterRequestTimestamp = immediate ? 0 : (new Date()).getTime();
            if (immediate) {
                this.affiliatesFilterChecker();
            }
        },

        async affiliatesFilterChecker() {
            if (!this.isAffiliatesFilterRunning && this.affiliatesFilterRequested) {
                const inputFinished = (new Date()).getTime() > this.affiliatesFilterRequestTimestamp + SEARCH_CONTINUOUS_INPUT_MS;
                if (inputFinished) {
                    return this.applyAffiliatesFilter();
                }
            }
        },

        async applyAffiliatesFilter() {
            if (this.isAffiliatesFilterRunning) {
                this.requestAffiliatesFilter();
                return;
            }
            this.affiliatesFilterRequested = false;
            this.affiliatesFilterRequestTimestamp = 0;
            this.isAffiliatesFilterRunning = true;

            let filteredAffiliates = [];

            // First, perform filter by type
            let typeFilteredAffiliates = this.affiliates.filter(aff => this.affiliateTypes.some(t => aff.types.includes(t)));

            if (this.location) {
                let typeCountryFilteredAffiliates = [];

                if (this.location.countryCode) {
                    // Secondly, perform filter by country if there are any
                    typeCountryFilteredAffiliates = typeFilteredAffiliates.filter(aff => {
                        return !this.location?.countryCode || aff.countries.some(c => c.a2 === this.location.countryCode)
                    });
                }

                if (this.location?.isUserLocation || !typeCountryFilteredAffiliates.length) {
                    // When user location is active
                    // Discard country filter
                    // Perform in-bounds lookup
                    if (this.location?.bounds) {
                        // Use rectangular location bounds if available
                        filteredAffiliates = typeFilteredAffiliates.filter(aff => {
                            return isValidLatLng(aff.location) && this.location.bounds.contains(aff.location);
                        });
                    }

                    if (this.gmaps && this.location && (this.location?.isUserLocation || filteredAffiliates.length < MIN_NUM_LOCATION_DISTANCE_RESULTS)) {
                        // If we're still getting less results than MIN distance based results
                        // Try to get more results by increasing radius and performing nearest neighbour lookup
                        const numberLeft = MIN_NUM_LOCATION_DISTANCE_RESULTS - filteredAffiliates.length;

                        // Perform radius search
                        const affiliatesWithDistances = [];
                        typeFilteredAffiliates.forEach(affiliate => {
                            if (!filteredAffiliates.find(aff => aff.id === affiliate.id) && isValidLatLng(affiliate.location)) {
                                affiliatesWithDistances.push({
                                    affiliate,
                                    distance: this.gmaps.geometry.spherical.computeDistanceBetween(this.location, affiliate.location),
                                });
                            }
                        });
                        affiliatesWithDistances.sort((a, b) => {
                            return a.distance > b.distance ? 1 : (a.distance < b.distance ? -1 : 0);
                        });
                        const results = {};
                        for (let res of affiliatesWithDistances) {
                            if (res.distance < MAX_RADIUS_ANY_NUM_AFFILIATES || Object.keys(results).length < numberLeft) {
                                results[res.affiliate.id] = res;
                            } else {
                                break;
                            }
                        }
                        const neighbors = {};
                        Object.keys(results).forEach(key => {
                            const res = results[key];
                            affiliatesWithDistances.forEach(other => {
                                if (
                                    !results[other.affiliate.id] &&
                                    !neighbors[other.affiliate.id] &&
                                    Math.abs(other.distance - res.distance) <= MAX_RADIUS_NEIGHBORS_LOOKUP
                                ) {
                                    neighbors[other.affiliate.id] = other;
                                }
                            });
                        });
                        filteredAffiliates = filteredAffiliates.concat(
                            Object.values(results)
                                .concat(Object.values(neighbors))
                                .map(res => res.affiliate)
                        );
                    }
                }

                if (!filteredAffiliates.length) {
                    filteredAffiliates = typeCountryFilteredAffiliates;
                }

                // Forcibly add affiliates which are associated with a given country
                if (this.location.countryCode) {
                    const filteredIds = filteredAffiliates.map(aff => aff.id);
                    for (let aff of typeFilteredAffiliates) {
                        if (aff.countries.slice(1).some(c => c.a2 === this.location.countryCode) && !filteredIds.includes(aff.id)) {
                            filteredAffiliates.push(aff);
                        }
                    }
                }

                const centerLatLng = this.location.bounds?.getCenter() || this.location;
                for (let aff of filteredAffiliates) {
                    aff._deltaL = centerLatLng && isValidLatLng(aff.location)
                                  ? google.maps.geometry.spherical.computeDistanceBetween(centerLatLng, aff.location)
                                  : null;
                    aff._deltaU = this.location.isUserLocation ? aff._deltaL : null;
                }

                // Sort affiliates by proximity
                filteredAffiliates.sort((a, b) => {
                    return a._deltaL > b._deltaL ? 1 : (a._deltaL < b._deltaL ? -1 : 0);
                });

            } else {
                typeFilteredAffiliates.forEach(aff => {
                    aff._deltaL = null;
                    aff._deltaU = null;
                });
                filteredAffiliates = typeFilteredAffiliates;
            }

            this.filteredAffiliates = filteredAffiliates;

            return new Promise(resolve => {
                this.$nextTick(() => {
                    this.isAffiliatesFilterRunning = false;
                    this.affiliatesFilterChecker();
                    resolve();
                });
            });
        },

        centerMapOnAffiliates(affiliates = [], args = {}) {
            const { location } = Object.assign({}, { location: this.location }, args);

            /** @var {google.maps.Map} map */
            const map = this.$refs.gmap?.$mapObject;
            if (!map || !affiliates.length) {
                return;
            }
            const boundingBox = new google.maps.LatLngBounds();
            const extendByAffiliateLocation = affiliate => {
                isValidLatLng(affiliate.location) && boundingBox.extend(affiliate.location);
            };
            affiliates.forEach(extendByAffiliateLocation);

            if (location) {
                boundingBox.extend(location);
            }

            google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                if (this.forcedMapRefresgTimeoutId) {
                    window.clearTimeout(this.forcedMapRefresgTimeoutId);
                    this.forcedMapRefresgTimeoutId = null;
                }
                this.forcedMapRefresgTimeoutId = setTimeout(() => {
                    this.mapZoom = map.getZoom();
                    this.mapCenter = map.getCenter();
                    this.forcedMapRefresgTimeoutId = null;
                }, GMAPS_FORCE_REFRESH_DELAY_MS);
            });
            map.fitBounds(boundingBox);
        },

        showAffiliateOnMap(affiliate) {
            this.activeOutputTab = 'map';
            this.$nextTick(() => {
                this.centerMapOnAffiliates([affiliate], { location: null });
            });
        },

        onWindowResize() {
            this.isMobile = isMobile();
            this.updateResetButtonPosition();
            this.onFilterEnter();
        },

        updateResetButtonPosition() {
            const resetBtn = this.$refs.resetBtn;
            const searchInput = this.$refs.searchInput;
            if (resetBtn && searchInput) {
                resetBtn.style.right = `${searchInput.offsetWidth}px`;
                resetBtn.style.height = `${searchInput.offsetHeight}px`;
            }
        },

        onDocumentClick(event) {
            if (
                this.filtersVisible &&
                this.$refs.vmfWrapper &&
                this.$refs.vmfToggleBtn &&
                event.target !== this.$refs.vmfWrapper &&
                event.target !== this.$refs.vmfToggleBtn &&
                !this.$refs.vmfWrapper.contains(event.target) &&
                !this.$refs.vmfToggleBtn.contains(event.target)
            ) {
                this.filtersVisible = false;
            }
        },

        onFilterEnter() {
            if (this.$refs.vmfWrapper && this.$refs.vmfInner) {
                this.$refs.vmfWrapper.style.height = this.$refs.vmfInner.scrollHeight + 'px';
            }
        },

        onFilterLeave() {
            if (this.$refs.vmfWrapper) {
                this.$refs.vmfWrapper.style.height = '';
            }
        },

        getGmapsClusterStyles(variation = '') {
            const themeUrl = this.$rawProps.api?.theme_url || '';
            const appendName = variation ? `-${variation}` : '';
            const textColor = 'white';
            const theBiggest = {
                textColor,
                width: 54,
                height: 54,
                url: `${themeUrl}/assets/images/gmaps/m3${appendName}.png`
            };
            return [
                {
                    textColor,
                    width: 36,
                    height: 36,
                    url: `${themeUrl}/assets/images/gmaps/m1${appendName}.png`
                },
                {
                    textColor,
                    width: 48,
                    height: 48,
                    url: `${themeUrl}/assets/images/gmaps/m2${appendName}.png`
                },
                theBiggest,
                theBiggest,
                theBiggest,
            ]
        },

        isGeolocationAvailable() {
            return 'geolocation' in window.navigator;
        },

        toggleUserLocation() {
            if (this.location?.isUserLocation) {
                this.resetLocationSearch();
            } else {
                this.searchByUserLocation();
            }
        },

        searchByUserLocation() {
            if (this.isGeolocationAvailable()) {
                this.isQueryingLocation = true;
                window.navigator.geolocation.getCurrentPosition(async position => {
                    let formattedAddress = '';
                    try {
                        const geocoder = new google.maps.Geocoder();
                        const response = await geocoder.geocode({
                            location: {
                                lat: position.coords.latitude,
                                lng: position.coords.longitude,
                            },
                        });
                        if (response?.results?.length) {
                            formattedAddress = response.results[0].formatted_address;
                        }
                    } catch (err) {
                        console.warn(err);
                    }

                    this.locationInput = formattedAddress;
                    this.location = {
                        lat: position.coords.latitude || 0,
                        lng: position.coords.longitude || 0,
                        isUserLocation: true,
                        formattedAddress,
                    };

                    this.isQueryingLocation = false;
                });
            }
        },

        async locateAndSearch() {
            if (!this.locationInput || this.isQueryingLocation) {
                return;
            }
            if (this.location?.formattedAddress === this.locationInput) {
                this.centerMapOnAffiliates(this.filteredAffiliates);
                return;
            }
            this.isQueryingLocation = true;
            try {
                const geocoder = new google.maps.Geocoder();
                const response = await geocoder.geocode({
                    address: this.locationInput,
                });
                if (response?.results?.length) {
                    const res = response.results[0];
                    const location = {
                        formattedAddress: res.formatted_address,
                        bounds: res.geometry.bounds,
                        lat: res.geometry.location.lat() || 0,
                        lng: res.geometry.location.lng() || 0,
                    };
                    const countryCmp = res.address_components.find(cmp => cmp.types.includes('country'));
                    if (countryCmp) {
                        location.countryCode = countryCmp.short_name;
                    }
                    this.locationInput = res.formatted_address;
                    this.location = location;
                } else {
                    this.locationNotFound = true;
                }
            } catch (err) {
                console.warn(err);
                this.locationNotFound = true;
            }
            this.isQueryingLocation = false;
        },

        resetLocationSearch() {
            this.location = null;
        },

        resetAllSearch() {
            this.resetLocationSearch();
            this.locationInput = '';
            this.requestAffiliatesFilter();
        },

        onGmapAutocomplete(data) {
            if (data && data.geometry) {
                this.location = {
                    lat: data.geometry.location.lat() || 0,
                    lng: data.geometry.location.lng() || 0,
                };
            }
        },

        resetListView() {
            this.$nextTick(() => {
                this.$refs?.listView?.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            });
        },

        toggleAffiliateType(affiliateType) {
            if (this.affiliateTypes.includes(affiliateType)) {
                if (this.affiliateTypes.length > 1) {
                    this.affiliateTypes = this.affiliateTypes.filter(vt => vt !== affiliateType);
                }
            } else {
                this.affiliateTypes = this.affiliateTypes.concat(affiliateType);
            }
        },
    },
});
</script>

<template>
    <div class="affiliateMap" :class="{'is--unavailable': isUnavailable}">

        <transition name="busy">
            <div class="busy" v-if="isUnavailable">
                <i class="icon far fa-sync-alt" aria-hidden="true"></i>
            </div>
        </transition>

        <section class="search" :class="{'is--active': this.gmaps, 'has--geolocation': isGeolocationAvailable()}">
            <div class="searchRow" :class="{'in--focus': locationInputInFocus}">
                <input
                    class="searchInput"
                    :class="{ 'has--value': locationInput }"
                    type="text"
                    ref="searchInput"
                    v-model="locationInput"
                    :placeholder="$str('location_input_placeholder')"
                    @focus="locationInputInFocus = true"
                    @blur="locationInputInFocus = false"
                    @keyup.enter="locateAndSearch"
                />
                <button
                    class="resetBtn"
                    ref="resetBtn"
                    :title="$str('actions.show_all')"
                    v-if="location || locationInput"
                    @click="resetAllSearch"
                >
                    <i class="icon fa-regular fa-xmark" aria-hidden="true"></i>
                </button>
                <button
                    class="searchBtn"
                    :title="$str('actions.search')"
                    @click="locateAndSearch"
                    @focus="locationInputInFocus = true"
                    @blur="locationInputInFocus = false"
                >
                    <i class="icon fa-regular fa-search" aria-hidden="true"></i>
                </button>
                <button
                    class="userLocationBtn"
                    :class="{'is--active': isUserLocation}"
                    v-if="isGeolocationAvailable()"
                    :title="$str('use_my_location')"
                    @focus="locationInputInFocus = true"
                    @blur="locationInputInFocus = false"
                    @click="toggleUserLocation"
                >
                    <i class="icon fa-regular fa-location-arrow" aria-hidden="true"></i>
                </button>
            </div>

            <div class="typesRow">
                <button
                    :class="{ 'is--active': affiliateTypes.includes($affiliateTypes.dealer) }"
                    @click="toggleAffiliateType($affiliateTypes.dealer)"
                >
                    <i class="icon fa-regular fa-check" aria-hidden="true"></i>
                    <span class="txt">{{ $str('dealer.plural') }}</span>
                </button>
                <button
                    :class="{ 'is--active': affiliateTypes.includes($affiliateTypes.service) }"
                    @click="toggleAffiliateType($affiliateTypes.service)"
                >
                    <i class="icon fa-regular fa-check" aria-hidden="true"></i>
                    <span class="txt">{{ $str('service.plural') }}</span>
                </button>
            </div>
        </section>

        <div class="tabs" v-if="isMobile">
            <button
                :class="{'is--active': activeOutputTab === 'list'}"
                @click="activeOutputTab = 'list'"
            >
                <span class="icon fa-regular fa-list-alt"></span>
                <span class="txt">{{ $str('tabs.list') }}</span>
            </button>
            <button
                :class="{'is--active': activeOutputTab === 'map'}"
                @click="activeOutputTab = 'map'"
            >
                <span class="icon fa-regular fa-globe"></span>
                <span class="txt">{{ $str('tabs.map') }}</span>
            </button>
        </div>

        <section class="map" v-show="!isMobile || activeOutputTab === 'map'">
            <GMapMap
                ref="gmap"
                :center="mapCenter"
                :zoom="mapZoom"
                :options="{
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: true,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: true,
                    mapId: $rawProps.googleMaps?.map_id,
                    maxZoom: 19,
                }"
            >
                <GMapMarker
                    v-if="isUserLocation"
                    :position="location"
                    :icon="{
                        url: `${$rawProps.api?.theme_url}/assets/images/gmaps/marker-location.png`,
                        scaledSize: { width: 32, height: 32 },
                        labelOrigin: { x: 16, y: 16 },
                    }"
                    :clickable="false"
                    :draggable="false"
                />
                <GMapCluster
                    v-if="filteredAffiliates.length"
                    :minimum-cluster-size="2"
                    :styles="getGmapsClusterStyles('v2')"
                    :zoom-on-click="true"
                >
                    <template
                        v-for="affiliate of filteredAffiliates"
                        :key="affiliate.id"
                    >
                        <GMapMarker
                            v-if="affiliate.location?.lat && affiliate.location?.lng"
                            :position="affiliate.location"
                            :icon="{
                                url: `${$rawProps.api?.theme_url}/assets/images/gmaps/marker-v2.png`,
                                scaledSize: { width: 36, height: 36 },
                                labelOrigin: { x: 18, y: 36 },
                            }"
                            :clickable="true"
                            :draggable="false"
                            @click="affiliateToContact = affiliate"
                        />
                    </template>
                </GMapCluster>
            </GMapMap>
        </section>

        <section class="results" v-if="!isMobile || activeOutputTab === 'list'">

            <div class="countries" v-if="filteredAffiliates.length">
                <div
                    v-for="country of countriesWithAffiliates"
                    :key="country.a2"
                    class="country"
                    :class="{ 'has--multipleAffiliates': country.affiliates.length > 1 }"
                >
                    <header>
                        <img class="flag" :src="`https://flagcdn.com/${country.a2.toLowerCase()}.svg`" :alt="country.name"/>
                        <h3>{{ country.name }}</h3>
                    </header>
                    <div class="affiliates">
                        <div
                            v-for="(affiliate, idx) of country.affiliates"
                            :key="affiliate.searchKey || affiliate.id"
                            class="affiliateWrapper"
                        >
                            <AffiliateCard :affiliate="affiliate" @show-contact-form="affiliateToContact = affiliate"/>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="nothingFound">
                <div>{{ $str('nothing_found') }}</div>
                <button @click="resetAllSearch">
                    <i class="far fa-undo-alt" aria-hidden="true"></i>
                    <span>{{ $str('actions.show_all') }}</span>
                </button>
            </div>
        </section>

        <BasicModal
            class="cardModal"
            attach="body"
            v-model="locationNotFound"
            :focus-retain="false"
            :focus-trap="false"
            @cancel="locationNotFound = false"
        >
            <template #title>
                {{ $str('sorry_address_{address}_not_found').replace('{address}', locationInput) }}
            </template>
            <template #actions><span></span></template>
        </BasicModal>

        <BasicModal
            attach="body"
            v-if="affiliateToContact"
            v-model="affiliateToContact"
            :focus-retain="false"
            :focus-trap="false"
            @cancel="affiliateToContact = null"
        >
            <template #title>
                {{ $str('contact_request_{affiliate}').replace('{affiliate}', affiliateToContact.title) }}
            </template>

            <ContactForm
                v-if="affiliateToContact"
                :affiliate="affiliateToContact"
                @cancel="affiliateToContact = null"
            />

            <template #actions><span></span></template>
        </BasicModal>

    </div>
</template>





<style lang="scss" scoped>
@import "defs";

.affiliateMap {
    position: relative;
    margin: 4rem 0;
    z-index: 5;
    font-size: 1.6rem;

    &.is--unavailable {}

    a {
        color: black;
    }

    .busy {
        @include absolute-fit;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100;
        background-color: rgba(white, 0.75);
        font-size: 2rem;
        .icon {
            color: $c_primary;
            animation: {
                name: spin;
                duration: 2s;
                timing-function: linear;
                iteration-count: infinite;
            };
        }
        &.busy-enter-active,
        &.busy-leave-active {
            transition: $transition_side;
        }

        &.busy-enter-from,
        &.busy-leave-to {
            opacity: 0;
        }
    }
}

.cardModal {
    ::v-deep(.bm-container) {
        .bm-content {
            max-width: 900px;
        }
        .showOnMap {
            display: none;
        }
    }
}

.search {
    .searchRow {
        display: flex;
        align-items: stretch;
        justify-content: flex-start;
        overflow: hidden;
        padding: 0;
        margin: 2rem 0;
        transition: $transition_fast;
    }

    .searchInput {
        min-width: 50%;
        padding-right: 5rem;
        border-radius: 8px;
        font-size: 1.8rem;
        font-weight: 700;
        color: $c_dark;
        border: 2px solid $c_gray_lightest;
        box-shadow: none !important;
        outline: none !important;
        transition: $transition_fast;
        &::placeholder {
            color: $c_gray;
            font-weight: 300;
            transition: $transition_fast;
        }
        &:hover, &:focus {
            color: $c_darkest;
            border-color: $c_gray;
            &::placeholder {
                color: $c_darkest;
            }
        }
        &:focus,
        &.has--value {
            border-color: $c_dark;
            box-shadow: 0 1px 5px rgba(black, 0.25);
        }
    }

    .searchBtn,
    .userLocationBtn,
    .resetBtn {
        padding: 0 2rem;
        margin-left: 1.6rem;
        border-radius: 8px;
        font-size: 1.8rem;
    }

    .resetBtn {
        position: absolute;
        top: 0;
        color: $c_gray;
        &:hover, &:focus {
            color: black;
            background-color: rgba(black, 0.05);
        }
    }

    .searchBtn {
        padding: 0 4rem;
        background-color: $c_primary;
        &:hover, &:focus {
            background-color: $c_primary_lighter;
        }
    }

    .userLocationBtn {
        color: $c_dark;
        border: 1px solid $c_gray;
        &:not(.is--active) {
            &:hover, &:focus {
                background-color: $c_lightest;
                border-color: $c_dark;
                color: black;
            }
        }
    }

    .userLocationBtn {
        &.is--active {
            border-color: black;
            background-color: black;
            color: $c_primary;
            &:hover, &:focus {
                background-color: $c_dark;
                border-color: $c_dark;
            }
        }
    }

    .typesRow {
        display: flex;
        align-items: stretch;
        margin: 2rem 0;

        button {
            font-weight: 400;
            color: $c_dark;
            & + button {
                margin-left: 2rem;
            }
            .icon {
                margin-right: 8px;
                padding: 2px;
                color: transparent;
                border-radius: 4px;
                border: 1px solid $c_gray;
                transition: $transition_fast;
            }
            &:hover, &:focus {
                .icon {
                    border-color: $c_dark;
                }
            }
            &.is--active {
                .icon {
                    color: black;
                    background-color: $c_primary;
                    border-color: $c_primary;
                }
                &:hover, &:focus {
                    .icon {
                        background-color: $c_primary_darker;
                        border-color: $c_primary_darker;
                    }
                }
            }
        }
    }
}

.map {
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 40%;
    ::v-deep(.vue-map-container) {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
    ::v-deep(.cluster) {
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 1.35rem;
    }
}

.results {
    margin-top: 2rem;

    .countries {
        display: flex;
        align-items: stretch;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 1rem 2rem;
    }

    .country {
        width: calc(50% - 1rem);
        margin-top: 2.4rem;

        header {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            margin-bottom: 1rem;
            .flag {
                height: auto;
                width: 48px;
                margin-right: 1rem;
                border: 1px solid $c_light;
            }
            h3 {
                margin: 0;
                font-size: 3rem;
                font-weight: 300;
                color: $c_gray_lighter;
            }
        }

        .affiliates {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;
            gap: 2rem;
            position: relative;
            width: 100%;
            z-index: 1;
            overflow-y: auto;
            @media (min-width: 2000px) {
                display: flex;
                flex-direction: row;
                align-items: stretch;
                justify-content: space-between;
                flex-wrap: wrap;
            }
        }

        .affiliateWrapper {
            width: 100%;
            .affiliate {
                height: 100%;
            }
        }

        &.has--multipleAffiliates {
            width: 100%;
            .affiliateWrapper {
                width: calc(50% - 1rem);
            }
        }
    }

    .nothingFound {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        font-size: 1rem;

        button {
            margin: 1rem 0 0;
            padding: 0.5em 1em;
            font-weight: 700;
            color: black;

            .icon {
                margin-right: 0.5em;
                color: $c_primary;
            }
        }
    }
}

@media (max-width: 900px) {
    .tabs {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        justify-content: space-between;
        border-bottom: 4px solid $c_dark;

        button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 50%;
            margin: 0;
            padding: 0.5em 1em;
            background-color: $c_lighter;

            .txt, .icon {
                display: block;
            }

            .txt {
                margin-left: 0.5em;
            }

            .icon {
                font-size: 1.5em;
                color: $c_gray;
            }

            &.is--active {
                background: $c_primary;
                .icon {
                    color: black;
                }
            }
        }
    }
    .map,
    .results {
        width: 100%;
    }

    .map {
        height: calc(15rem + 30vh + 12vw);
    }

    .results {
        .countries {
            flex-direction: column;
            gap: 1rem 2rem;
        }
        .country {
            width: 100%;
        }
    }
}

@media (max-width: 720px) {
    .search {
        .searchRow {
            .searchInput {
                width: 100%;
            }
        }
    }
    .results {
        .affiliates {
            .affiliateWrapper {
                width: 100% !important;
            }
        }
    }
}

@media (max-width: 480px) {
    .search {
        .searchRow {
            flex-wrap: wrap;
            .searchInput {
                margin-bottom: 2rem;
            }
            button {
                width: calc(33.33% - 1.2rem);
                padding: 1rem 2rem;
                & + button {
                    margin-left: 1.8rem;
                }
            }
            .searchBtn {
                margin-left: 0;
            }
        }
    }
}

</style>
